import React, { useEffect } from 'react';
import Card from 'components/card';
import { MdCheckCircle, MdEmail, MdSimCardDownload } from 'react-icons/md';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import { dateToFrenchShort } from 'utils/date';
import { downloadInvoice } from 'interfaces/invoice';
import { getCustomerAnalytics } from 'interfaces/customer';
import { IoMdEye } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';

type RowObj = {
    name: string;
    status: string;
    date: string;
    progress: number;
    invoiceNumber: string;
    invoicedAt: string;
    _id: string;
    totalHT: number;
    totalTTC: number;
};

const columnHelper = createColumnHelper<RowObj>();

// const columns = columnsDataCheck;
export default function LastInvoices(props: { tableData: any }) {
    const { tableData } = props;
    const [sorting, setSorting] = React.useState<SortingState>([]);
    let defaultData = tableData;
    const nav = useNavigate()

    const fetchInvoice = async (id: string) => {
        try {
            const blob = await downloadInvoice(id);
            if (!blob) return;

            const invoice = data.find((invoice: any) => invoice._id === id);
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `${invoice?.to.company}-${invoice?.invoiceNumber}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const columns = [
        columnHelper.accessor('invoiceNumber', {
            id: 'invoiceNumber',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    N° Facture
                </p>
            ),
            cell: (info) => (
                <>
                    <p className="text-xs text-navy-700 dark:text-white">
                        #{info.getValue()}
                    </p>
                    <p className="text-xs font-bold text-navy-700 dark:text-white">
                        {dateToFrenchShort(info.row.original.invoicedAt)}
                    </p>
                </>
            ),
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Statut
                </p>
            ),
            cell: (info) => (
                <div className="flex items-center">
                    {info.getValue() === 'generated' && (
                        <div className="flex bg-red-400 rounded-xl p-1 px-2 gap-2">
                            {/* <MdOutlineError className="text-white me-1 dark:text-amber-300" /> */}
                            <p className="text-[10px] text-white dark:text-white">
                                Non envoyée
                            </p>
                        </div>
                    )}
                    {info.getValue() === 'sent' && (
                        <div className="flex bg-amber-500 rounded-xl p-1 px-2 gap-1">
                            {/* <MdOutlineError className="text-white me-1 dark:text-amber-300" /> */}
                            <MdEmail className="text-white me-1 dark:text-white" />
                            <p className="text-[10px] text-white">Envoyée</p>
                        </div>
                    )}
                    {info.getValue() === 'paid' && (
                        <div className="flex bg-green-500 rounded-xl p-1 px-2 gap-1">
                            <MdCheckCircle className=" text-white" />
                            <p className="text-[10px] text-white dark:text-white">
                                Payée
                            </p>
                        </div>
                    )}
                    {info.getValue() === 'canceled' && (
                        <div className="flex bg-gray-300 rounded-xl p-1 px-2 gap-2">
                            {/* <MdOutlineError className="text-white me-1 dark:text-amber-300" /> */}
                            <p className="text-[10px] text-white dark:text-white">
                                Annulée
                            </p>
                        </div>
                    )}
                    {info.getValue() === 'credit-note' && (
                        <div className="flex bg-blue-400 rounded-xl p-1 px-2 gap-2">
                            {/* <MdOutlineError className="text-white me-1 dark:text-toola-300" /> */}
                            <p className="text-[10px] text-white">
                                Facture d'avoir
                            </p>
                        </div>
                    )}
                    {/* <MdCancel className="text-red-500 me-1 dark:text-red-300" /> */}
                </div>
            ),
        }),
        columnHelper.accessor('totalHT', {
            id: 'totalHT',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Total HT
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-medium text-navy-700 dark:text-white">
                    {info.getValue().toFixed(2)} €
                </p>
            ),
        }),
        columnHelper.accessor('totalTTC', {
            id: 'totalTTC',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Total TTC
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-medium text-navy-700 dark:text-white">
                    {info.getValue().toFixed(2)} €
                </p>
            ),
        }),
        columnHelper.accessor('_id', {
            id: '_id',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Actions
                </p>
            ),
            cell: (info) => (
                <div className="flex items-center">
                    <MdSimCardDownload
                        title="Télécharger la facture"
                        className="text-toola-500 h-6 w-6 hover:cursor-pointer hover:text-toola-700"
                        onClick={() => fetchInvoice(info.getValue())}
                    />
                    <IoMdEye
                        title="Voir la facture"
                        className="text-toola-500 h-6 w-6 hover:cursor-pointer hover:text-toola-700"
                        onClick={() => {
                            const year = new Date(info.row.original.invoicedAt).getFullYear();
                            nav(`/invoices/all/${year}?search=${info.row.original.invoiceNumber}`)}
                        }
                    />
                </div>
            ),
        }),
    ]; // eslint-disable-next-line
    const [data, setData] = React.useState(() => [...defaultData]);
    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        debugTable: true,
    });

    return (
        <Card extra={'w-full h-full px-6 pb-6 sm:overflow-x-auto border'}>
            <div className="mt-5 w-full px-[8px]">
                <h4 className="text-lg font-medium text-navy-700 dark:text-white">
                    Dernières factures
                </h4>
            </div>

            <div className="mt-2 overflow-x-scroll xl:overflow-x-hidden">
                <table className="w-full">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr
                                key={headerGroup.id}
                                className="!border-px !border-gray-400"
                            >
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            onClick={header.column.getToggleSortingHandler()}
                                            className="cursor-pointer border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30"
                                        >
                                            <div className="items-center justify-between text-xs text-gray-200">
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                                {{
                                                    asc: '',
                                                    desc: '',
                                                }[
                                                    header.column.getIsSorted() as string
                                                ] ?? null}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table
                            .getRowModel()
                            .rows.slice(0, 5)
                            .map((row) => {
                                return (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td
                                                    key={cell.id}
                                                    className="min-w-[90px] border-white/0 py-2 px-2"
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef
                                                            .cell,
                                                        cell.getContext(),
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
                {table.getRowModel().rows.length === 0 && (
                    <div className="flex items-center justify-center h-32">
                        <p className="text-gray-400 dark:text-white">
                            Aucune facture
                        </p>
                    </div>
                )}
            </div>
        </Card>
    );
}
